/**
 * Player switching — the PURE selection + validity math. (§4.8)
 *
 * This module is stateless: every function is a pure function of the world it is
 * given. It is the ONE home for "which player should control move to" so that the
 * two authorities that make that decision compute it identically:
 *
 *   - the SERVER, when it honours InputFlags.SwitchPlayer or auto-switches on a
 *     possession change (room.ts) — this is the authoritative path online;
 *   - the CLIENT, when there is no server (Practice vs COM offline) or to move
 *     its own belief optimistically ahead of the server's ControlAssign
 *     (game/playerSwitch.ts).
 *
 * Both import THIS. Before this file existed the cone-scoring lived inline in
 * playerSwitch.ts and nothing on the server matched it, so a switch the client
 * predicted was not the switch the server would have made. Sharing the math is
 * what makes the optimistic client switch and the authoritative server switch
 * converge instead of fight.
 *
 * ── Why it does not live in sim/constants or sim/ ──────────────────────────
 *
 * Switching is not part of the deterministic tick — step() never reads
 * InputFlags.SwitchPlayer (grep-verified). It is a control-plane decision made
 * OUTSIDE step(), so it belongs beside the sim, not inside it. SWITCH_COOLDOWN_MS
 * lives here rather than in sim/constants.ts for the same reason (and because that
 * file is owned by the concurrent sim wave); it is a switch-debounce, not a knob
 * a designer turns to change how football feels.
 */

import {
  MANUAL_SWITCH_CONE_DEG,
  MAX_PLAYERS,
  SWITCH_BALL_RELEVANCE_WEIGHT,
  SWITCH_DISTANCE_FALLOFF,
  SWITCH_DISTANCE_WEIGHT,
  type WorldState,
} from './sim/index.js';
import { DEG2RAD } from './math/index.js';

/**
 * Minimum gap between two honoured switches from one connection (§4.8 implied —
 * "a clear, animated ring... must be instantly legible", which a switch every
 * tick is not). Edge-triggering already stops a held button re-firing; this is
 * the belt to that suspenders, bounding a client that toggles the flag every
 * other tick. 250ms is a quarter-second — below a deliberate double-tap, above
 * anything the eye reads as one switch.
 */
export const SWITCH_COOLDOWN_MS = 250;

/** A flick / desired-switch direction in world XZ. Need not be normalized. */
export interface SwitchDir {
  readonly x: number;
  readonly z: number;
}

/** Guard acos against a dot product floating point nudged just past ±1. */
function clampCos(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

/**
 * §4.8 validity predicate: is `id` a legal player to hand control to?
 *   - same team as the switcher,
 *   - active and not sent off,
 *   - outfield — the keeper is a specialist and switching a defender into the
 *     keeper because the ball is in the six-yard box is exactly the FIFA bug
 *     people shout about (unless `allowGK`, e.g. a mode that lets you take the GK).
 *
 * It does NOT know about other humans — "never steal a slot another human drives"
 * is a control-plane fact the caller holds (see the `excluded` mask on
 * selectSwitchTarget), not something the WorldState can express.
 */
export function isValidSwitchTarget(
  world: WorldState,
  team: number,
  id: number,
  allowGK = false,
): boolean {
  if (id < 0 || id >= MAX_PLAYERS) return false;
  const p = world.players;
  if (p.active[id] !== 1) return false;
  if (p.sentOff[id] === 1) return false;
  if (p.team[id] !== team) return false;
  if (!allowGK && world.positions[id] === 'GK') return false;
  return true;
}

/**
 * Choose the player control should move to, or -1 if there is none.
 *
 * Two modes, keyed on `desiredDir`:
 *
 *   - DIRECTIONAL (a non-zero flick): cone-weighted selection in the flicked
 *     direction. Angle dominates — the flick is the player's stated intent, so a
 *     candidate they did not point at must never win on proximity alone — with
 *     distance and ball-relevance as tie-breakers. `currentId` is excluded (you
 *     flick AWAY to someone else).
 *
 *   - NEAREST-RELEVANT (`desiredDir` null or ~zero): the valid player nearest the
 *     ball. Used by auto-switch and by a directionless Tab-tap. `currentId` IS a
 *     candidate here, so a caller can detect "you are already on the nearest" by
 *     comparing the result to `currentId` (it will return `currentId` in that
 *     case rather than yanking control to a worse-placed player).
 *
 * @param excluded  Optional per-id mask; `excluded[id] === 1` skips that player.
 *                  This is how the server enforces "never steal a slot another
 *                  human drives" without leaking session state into this pure fn.
 */
export function selectSwitchTarget(
  world: WorldState,
  team: number,
  currentId: number,
  desiredDir: SwitchDir | null,
  excluded: Uint8Array | null = null,
  allowGK = false,
): number {
  const p = world.players;
  const dirLen = desiredDir === null ? 0 : Math.hypot(desiredDir.x, desiredDir.z);

  if (desiredDir !== null && dirLen > 1e-3) {
    const fx = desiredDir.x / dirLen;
    const fz = desiredDir.z / dirLen;
    const mx = p.px[currentId] ?? 0;
    const mz = p.pz[currentId] ?? 0;
    const halfCone = MANUAL_SWITCH_CONE_DEG * 0.5 * DEG2RAD;

    let best = -1;
    let bestScore = -Infinity;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (i === currentId) continue;
      if (!isValidSwitchTarget(world, team, i, allowGK)) continue;
      if (excluded !== null && excluded[i] === 1) continue;

      const dx = p.px[i]! - mx;
      const dz = p.pz[i]! - mz;
      const d = Math.hypot(dx, dz);
      if (d < 1e-3) continue;

      const dot = (dx / d) * fx + (dz / d) * fz;
      const angle = Math.acos(clampCos(dot));
      if (angle > halfCone) continue;

      const angleScore = 1 - angle / halfCone;
      const distScore = 1 / (1 + d / SWITCH_DISTANCE_FALLOFF);
      const ballDist = Math.hypot(p.px[i]! - world.ball.px, p.pz[i]! - world.ball.pz);
      const ballScore = 1 / (1 + ballDist / SWITCH_DISTANCE_FALLOFF);

      const score =
        angleScore + distScore * SWITCH_DISTANCE_WEIGHT + ballScore * SWITCH_BALL_RELEVANCE_WEIGHT;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    return best;
  }

  // Nearest-relevant: the valid player nearest the ball, currentId included.
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (!isValidSwitchTarget(world, team, i, allowGK)) continue;
    if (excluded !== null && excluded[i] === 1) continue;
    const dx = p.px[i]! - world.ball.px;
    const dz = p.pz[i]! - world.ball.pz;
    const d = Math.hypot(dx, dz);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}
